import axios from 'axios';
import store from '../../store/store'; // Import Redux Store

const baseURL = ''; // Podstawowy URL API
// const baseURL = 'https://api-dev.mapenapp.com/'; // Podstawowy URL API

const ApiService = axios.create({  
  baseURL: baseURL, // Podstawowy URL API
  timeout: 5000, // Limit czasu na odpowiedź (5 sekund)
});

// Interceptor żądań (np. dodawanie tokenów autoryzacyjnych)
ApiService.interceptors.request.use(
  async (config) => {
    const requestUrl = String(config?.url || '');
    const isAuthEndpoint =
      requestUrl.includes('/api/auth/login') ||
      requestUrl.includes('/api/auth/access-token');
    const hasAuthorizationHeader = Boolean(config?.headers?.Authorization);

    if (hasAuthorizationHeader && !isAuthEndpoint) {
      await ApiService.checkTokenExpiration();
      const token = store.getState().tokens.accessToken;
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    console.error('Błąd w żądaniu:', error);
    return Promise.reject(error);
  }
);

ApiService.checkTokenExpiration = async () => {
  const state = store.getState();
  const { refreshToken, accessTokenExpiration, refreshTokenExpiration } = state.tokens;

    // Aktualny czas w milisekundach
    const currentTime = Date.now();

    // Konwersja dat wygaśnięcia na milisekundy
    const accessTokenExpiresAtMs = new Date(accessTokenExpiration).getTime();
    const refreshTokenExpiresAtMs = new Date(refreshTokenExpiration).getTime();
    
  // Sprawdzenie, czy accessToken wygasł
  if (accessTokenExpiresAtMs && accessTokenExpiresAtMs < currentTime) {
    console.warn('Access token wygasł.');

    // Sprawdzenie, czy refreshToken jest ważny
    if (refreshTokenExpiresAtMs && refreshTokenExpiresAtMs > currentTime) {
      console.log('Odświeżanie tokenu...');
      try {
        await ApiService.refreshTokens(refreshToken);
        return true; // Tokeny zostały odświeżone
      } catch (error) {
        console.error('Błąd podczas odświeżania tokenu:', error);
        alert('Sesja wygasła. Zaloguj się ponownie.');
        store.dispatch({ type: 'tokens/clearTokens' }); // Wyczyszczenie tokenów w Redux
        window.location.href = '/login'; // Przekierowanie na stronę logowania
        return false;
      }
    } else {
      console.warn('Refresh token również wygasł. Wylogowywanie użytkownika...');
      alert('Sesja wygasła. Zaloguj się ponownie.');
      store.dispatch({ type: 'tokens/clearTokens' }); // Wyczyszczenie tokenów w Redux
      window.location.href = '/login'; // Przekierowanie na stronę logowania
      return false;
    }
  }

  console.log('Tokeny są ważne.');
  return true; // Tokeny są ważne
};

ApiService.refreshTokens = async (refreshToken) => {
  try {
    const response = await ApiService.get('/api/auth/access-token', {
      headers: {
        Authorization: `Bearer ${refreshToken}`, // Dodanie refreshToken do nagłówka
      },
    });

    const { token: accessToken, expiresAt: accessTokenExpiresAt } = response.data;

    // Zaktualizuj stan Redux
    store.dispatch({
      type: 'tokens/setAccessTokenWithExpiration',
      payload: {
        accessToken,        
        accessTokenExpiresAt,
      },
    });

    console.log('Token został odświeżony.');
    return { accessToken, accessTokenExpiresAt };
  } catch (error) {
    console.error('Błąd podczas odświeżania tokenu:', error);
    throw error;
  }
};

// Interceptor żądań (dodawanie tokenów autoryzacyjnych)
ApiService.getHeadersJsonUTF8OrDifferent = (contentType = 'application/json; charset=UTF-8') => {
  const token = store.getState().tokens.accessToken;

  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': contentType,
  };
};
ApiService.getHeaderOnlyToken = () => {
  const token = store.getState().tokens.accessToken;

  return {
    Authorization: `Bearer ${token}`,
  };
};


// Interceptor odpowiedzi (obsługa błędów)
ApiService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.code === 'ERR_CANCELED' || axios.isCancel?.(error)) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      switch (status) {
        case 400:
          console.error('Błąd 400: Nieprawidłowe żądanie', data);
          alert('Nieprawidłowe dane.');
          break;
        case 401:
          console.error('Błąd 401: Nieautoryzowany', data);
          alert('Sesja wygasła. Zaloguj się ponownie.');
          store.dispatch({ type: 'tokens/clearTokens' }); // Wyczyszczenie tokenów w Redux
          window.location.href = '/login'; // Przekierowanie na stronę logowania
          break;
        case 403:
          console.error('Błąd 403: Brak dostępu', data);
          alert('Nie masz uprawnień do wykonania tej operacji.');
          break;
        case 404:
          console.error('Błąd 404: Nie znaleziono zasobu', data);
          alert('Nie znaleziono zasobu.');
          break;
        case 500:
          console.error('Błąd 500: Błąd serwera', data);
          alert('Wystąpił błąd serwera. Spróbuj ponownie później.');
          break;
        default:
          console.error(`Błąd ${status}:`, data);
          alert('Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
      }
    } else if (error.request) {
      console.error('Brak odpowiedzi z serwera:', error.request);
      alert('Nie można połączyć się z serwerem. Sprawdź swoje połączenie internetowe.');
    } else {
      console.error('Błąd:', error.message);
      alert('Wystąpił błąd. Spróbuj ponownie.');
    }
    return Promise.reject(error);
  }
);

///-------------------------------------------------------------------------
// Funkcja logowania
ApiService.login = async (login, pass) => {
  try {
    const response = await ApiService.post('/api/auth/login', {
      login,
      pass,
    });
    return response.data; // Zwróć dane odpowiedzi (np. tokeny)
  } catch (error) {
    throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
  }
};
///-------------------------------------------------------------------------
// events

ApiService.getEvents = async (sortBy, sortDirection, pageNumber, searchQuery, filterQuery = null, pageSize = null) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const payload = {
            ...(sortBy !== null && sortDirection !== null && { sortBy }),
            ...(sortBy !== null && sortDirection !== null && { sortDirection }),
            ...(searchQuery !== null && { searchQuery }),
            ...(filterQuery !== null && { filterQuery }),
            ...(pageSize !== null && { pageSize }),
            pageNumber
        }
        const response = await ApiService.get('/api/events/paginated-sorted-and-with-search', {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
            params: payload,
        });
        return response.data; // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEvents2 = async () => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/only-not-approved', {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEventsTopCharts = async (path) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/top/' + path, {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEventsTop = async (timeScope, categoryId, sortCountingScope) => {
    await ApiService.checkTokenExpiration();

    const safeTimeScope = timeScope ?? 'ALL_TIME';
    const safeCategoryId = categoryId ?? 0;
    const safeSortScope = sortCountingScope ?? 'VIEWING';

    try {
        const response = await ApiService.get(
            `/api/events/top/${safeTimeScope}/${safeCategoryId}/${safeSortScope}`,
            {
                headers: ApiService.getHeaderOnlyToken(),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getEvent = async (eventId) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/' + eventId, {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getAllImagesForEvent = async (eventId) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events-images/by-event-id/' + eventId, {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.getEventsCategories = async () => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events-categories', {
            headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
        });
        return response // Zwróć dane użytkowników
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

ApiService.updateEvent = async (id, data) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.put('/api/events/'+ id, data, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.patchEvent = async (id, data) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/' + id, data, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.approveEvent = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/approve/' + id, null, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.rejectEvent = async (payload) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/reject', payload, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getPendingEventEdits = async () => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/events/pending-edits', {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.approvePendingEventEdit = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(`/api/events/pending-edits/approve/${id}`, null, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.rejectPendingEventEdit = async (payload) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch('/api/events/pending-edits/reject', payload, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.deleteEvent = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.delete('/api/events/' + id, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.deleteEventImage = async (id) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.delete('/api/events-images/'+ id, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.addNewEvent = async (data) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.post('/api/events', data, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.uploadEventImage = async (eventId, file) => {
    // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
    await ApiService.checkTokenExpiration();

    const formData = new FormData();
    formData.append('file', file); // Dodanie pliku do formData

    try {
        const response = await ApiService.post('/api/events-images/image-upload/' + eventId, formData, {
            headers: ApiService.getHeadersJsonUTF8OrDifferent('multipart/form-data'),
        });
        return response.data; // Zwróć ciąg znaków zwrócony przez endpoint
    } catch (error) {
        throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
    }
};

///-------------------------------------------------------------------------
// bunch-of-friends
ApiService.getBunchesByEventId = async (eventId) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/bunch-of-friends/by-event-id/${eventId}`, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

///-------------------------------------------------------------------------
// statistics
ApiService.getStatisticsSummary = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/summary', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsDemographics = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/demographics', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsTopCities = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/top-cities', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        if (error?.response?.status !== 404) {
            throw error;
        }

        try {
            const fallbackResponse = await ApiService.get('/api/v1/statistics/cities/top', {
                headers: ApiService.getHeaderOnlyToken(),
                params,
            });
            return fallbackResponse.data;
        } catch (fallbackError) {
            throw fallbackError;
        }
    }
};

ApiService.getStatisticsSeries = async (metricType, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/series/${metricType}`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsB2BSummary = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/b2b/summary', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsB2BSeries = async (metricType, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/b2b/series/${metricType}`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventsSummary = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/events/summary', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventsAverageParticipantsSeries = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/events/series/average-participants', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventsRanking = async (metricType, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/ranking/${metricType}`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsRankingsEvents = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/statistics/rankings/events', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.searchEventsForStatistics = async (params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get('/api/v1/events/search', {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventSummary = async (eventId, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/${eventId}/summary`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventDemographicsAge = async (eventId, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/${eventId}/demographics/age`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getStatisticsEventActivitySeries = async (eventId, params = {}) => {
    await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.get(`/api/v1/statistics/events/${eventId}/series/activity`, {
            headers: ApiService.getHeaderOnlyToken(),
            params,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getEventStatisticsViewing = async (eventId = null) => {
    await ApiService.checkTokenExpiration();

    const path = eventId ? `/api/event-statistics/viewing/${eventId}` : '/api/event-statistics/viewing';

    try {
        const response = await ApiService.get(path, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

ApiService.getBunchActivityChart = async (fromUtc, toUtc, eventId = null) => {
    await ApiService.checkTokenExpiration();

    const encodedFrom = encodeURIComponent(fromUtc);
    const encodedTo = encodeURIComponent(toUtc);
    const path = eventId
        ? `/api/bunch-statistics/activity-chart/${encodedFrom}/${encodedTo}/${eventId}`
        : `/api/bunch-statistics/activity-chart/${encodedFrom}/${encodedTo}`;

    try {
        const response = await ApiService.get(path, {
            headers: ApiService.getHeaderOnlyToken(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

///-------------------------------------------------------------------------
// password
ApiService.changePasswordWithOld = async (oldPassword, newPassword) => {
  await ApiService.checkTokenExpiration();

    try {
        const response = await ApiService.patch(
            '/api/password-changing/with-old-password',
            { oldPassword, newPassword },
            { headers: ApiService.getHeadersJsonUTF8OrDifferent() }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

///-------------------------------------------------------------------------
// system admin profiles
ApiService.getSystemAdminProfiles = async (params = {}, options = {}) => {
  await ApiService.checkTokenExpiration();

  const normalizedParams = {
    pageNumber: 1,
    pageSize: 10,
    roleScope: 'ALL_USERS',
    blockedOnly: false,
    ...params,
  };

  try {
    const response = await ApiService.get('/api/system_admin/profiles', {
      headers: ApiService.getHeaderOnlyToken(),
      params: normalizedParams,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

///-------------------------------------------------------------------------
// password recovery
ApiService.requestPasswordRecovery = async (email) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/password-recovery/${encodeURIComponent(email)}`, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

///-------------------------------------------------------------------------
// organizer registration (admin-assisted flow)
ApiService.registerOrganiser = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.post('/api/accounts/register-organiser', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOrganiserProfiles = async (params = {}, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/organiser/profiles', {
      headers: ApiService.getHeaderOnlyToken(),
      params,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOrganiserProfilesPaginated = async (params = {}, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/organiser/profiles/paginated', {
      headers: ApiService.getHeaderOnlyToken(),
      params,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOrganiserProfile = async (id, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/organiser/profiles/${id}`, {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.approveOrganiser = async (id) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.patch(`/api/organiser/approve/${id}`, null, {
      headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getOnlyNotApprovedOrganisers = async (options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/organiser/only-not-approved', {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getUserReports = async (options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get('/api/user/report', {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.getProfileById = async (id, options = {}) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.get(`/api/profiles/${id}`, {
      headers: ApiService.getHeaderOnlyToken(),
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.putWarning = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.put('/api/warning', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

ApiService.putBan = async (payload) => {
  await ApiService.checkTokenExpiration();

  try {
    const response = await ApiService.put('/api/ban', payload, {
      headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};



///-------------------------------------------------------------------------
///-------------------------------------------------------------------------
///-------------------------------------------------------------------------
///-------------------------------------------------------------------------
// users
//
// ApiService.getUsers = async (sortBy, sortDirection, pageNumber, role, searchQuery) => {
//     // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//     await ApiService.checkTokenExpiration();
//
//   try {
//     const payload = {
//       ...(sortBy !== null && sortDirection !== null && { sortBy }),
//       ...(sortBy !== null && sortDirection !== null && { sortDirection }),
//       ...(role !== null && { role }),
//       ...(searchQuery !== null && { searchQuery }),
//       pageNumber
//     }
//     const response = await ApiService.get('/api/admin/get-profiles', {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       params: payload,
//       });
//     return response.data; // Zwróć dane użytkowników
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };
//
// ApiService.getSportIcon = async (id) => {
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const response = await ApiService.get('/api/admin/sports/find-icon-by-id/' + id , {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//     });
//     return response.data;
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };
//
// ApiService.getUserData = async (id) => {
//     await ApiService.checkTokenExpiration();
//
//     try {
//         const response = await ApiService.get('/api/admin/profile/full_with_logins/' + id , {
//             headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//         });
//         return response.data;
//     } catch (error) {
//         throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//     }
// };

///-------------------------------------------------------------------------
// sports
//
// ApiService.getSports = async (sortBy, sortDirection, keyword, pageNumber) => {
//     // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//     await ApiService.checkTokenExpiration();
//
//   try {
//     const payload = {
//       ...(sortBy !== null && sortDirection !== null && { sortBy }),
//       ...(sortBy !== null && sortDirection !== null && { sortDirection }),
//       ...(keyword && { keyword }),
//       pageNumber
//     }
//     const response = await ApiService.get('/api/admin/sports/get-sports', {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       params: payload,
//       });
//     return response.data; // Zwróć dane użytkowników
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };
//
// ApiService.getTrainings = async (sortBy, sortDirection, keyword, pageNumber) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const payload = {
//       ...(sortBy !== null && sortDirection !== null && { sortBy }),
//       ...(sortBy !== null && sortDirection !== null && { sortDirection }),
//       ...(keyword && { keyword }),
//       pageNumber
//     }
//     const response = await ApiService.get('/api/admin/training', {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       params: payload,
//     });
//     return response.data; // Zwróć dane użytkowników
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };
//
// ApiService.getOpinionsU = async (sortBy, sortDirection, keyword, pageNumber) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const payload = {
//       ...(sortBy !== null && sortDirection !== null && { sortBy }),
//       ...(sortBy !== null && sortDirection !== null && { sortDirection }),
//       // ...(keyword && { keyword }),
//       pageNumber
//     }
//     const response = await ApiService.get('/api/opinion/partners', {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       params: payload,
//     });
//     return response.data; // Zwróć dane użytkowników
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };
//
// ApiService.getOpinionsT = async (sortBy, sortDirection, keyword, pageNumber) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const payload = {
//       ...(sortBy !== null && sortDirection !== null && { sortBy }),
//       ...(sortBy !== null && sortDirection !== null && { sortDirection }),
//       // ...(keyword && { keyword }),
//       pageNumber
//     }
//     const response = await ApiService.get('/api/opinion/trainings', {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       params: payload,
//     });
//     return response.data; // Zwróć dane użytkowników
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };
//
// ApiService.moveSportUp = async (sportOrder) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//   await ApiService.checkTokenExpiration();
//
// const token =  store.getState().tokens.accessToken;
// const path = `/api/admin/sports/order-move-up?order=${sportOrder}`;
// try {
//   const response = await ApiService.patch(path, {}, {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       });
// } catch (error) {
//   throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
// }
// };
//
// ApiService.moveSportDown = async (sportOrder) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//   await ApiService.checkTokenExpiration();
//
// const token =  store.getState().tokens.accessToken;
// const path = `/api/admin/sports/order-move-down?order=${sportOrder}`;
// try {
//   const response = await ApiService.patch(path, {}, {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       });
// } catch (error) {
//   throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
// }
// };
//
// ApiService.addNewSport = async (sportData) => {
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const response = await ApiService.post('/api/admin/sports/add-new', sportData, {
//       headers: ApiService.getHeadersJsonUTF8OrDifferent(),
//       });
//     return response.data;
//   } catch (error) {
//     throw error;
//   }
// };
//
// ApiService.geoCoding = async (longitude, latitude) => {
//   await ApiService.checkTokenExpiration();
//
//     try {
//         const response = await ApiService.get(`/api/resolve-location`, {
//             headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//             params: {
//                 longitude,
//                 latitude
//             }
//         });
//
//         return response.data; // Zwróć dane odpowiedzi
//     } catch (error) {
//         throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//     }
// }
//
// ApiService.addNewUser = async (data) => {
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const response = await ApiService.post('/api/admin/register', data, {
//       headers: ApiService.getHeadersJsonUTF8OrDifferent(),
//     });
//     return response.data;
//   } catch (error) {
//     throw error;
//   }
// };
//
// ApiService.toggleHasBlocked = async (userId) => {
//     await ApiService.checkTokenExpiration();
//
//     try {
//         const response = await ApiService.patch(`/api/admin/toggle-user-blocked/${userId}`, {}, {
//             headers: ApiService.getHeadersJsonUTF8OrDifferent(),
//         });
//         return null;
//     } catch (error) {
//         throw error;
//     }
// };
//
// ApiService.getHasBlocked = async (userId) => {
//     await ApiService.checkTokenExpiration();
//
//     try {
//         const response = await ApiService.get(`/api/admin/user-has-blocked/${userId}`,{
//             headers: ApiService.getHeadersJsonUTF8OrDifferent(),
//         });
//         return response.data;
//     } catch (error) {
//         throw error;
//     }
// };
//
// ApiService.getSportById = async (sportId) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const response = await ApiService.get(`/api/admin/sports/find-by/id/${sportId}`, {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       });
//     return response.data; // Zwróć dane odpowiedzi
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// }
// ApiService.toggleSportActive = async (sportId) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const response = await ApiService.patch(`/api/admin/sports/toggle-activation?id=${sportId}`, {}, {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       });
//     return response.data; // Zwróć dane odpowiedzi
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// }
// ApiService.updateSport = async (sportData) => {
//   await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//
//   try {
//     const response = await ApiService.post(`/api/admin/sports/update`, sportData, {
//       headers: ApiService.getHeadersJsonUTF8OrDifferent(),
//       });
//     return response.data;
//   } catch (error) {
//     throw error;
//   }
// };
//
// ApiService.deleteSportsByIdList = async (idList) => {
//   await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//
//   try {
//     const response = await ApiService.delete('/api/admin/sports/delete/by-id-list', {
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//       data: idList, // Lista ID w ciele zapytania
//     });
//
//     return response // Zwróć odpowiedź serwera
//   } catch (error) {
//     throw error; // Obsłuż błędy w miejscu wywołania
//   }
// };
//
// ApiService.setSortListEndpoint = async (sort) => {
// await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//
//   try {
//     const response = await ApiService.put('/api/admin/app_config/sport_sort_config/'+sort,{}, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//     });
//
//     return response // Zwróć odpowiedź serwera
//   } catch (error) {
//     throw error; // Obsłuż błędy w miejscu wywołania
//   }
// };
//
// ApiService.getSortListEndpoint = async () => {
// await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//
//   try {
//     const response = await ApiService.get('/api/admin/app_config/sport_sort_config',
//       {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       });
//     return response.data // Zwróć odpowiedź serwera
//   } catch (error) {
//     throw error; // Obsłuż błędy w miejscu wywołania
//   }
// };

///-------------------------------------------------------------------------
// images
// ApiService.uploadImage = async (file) => {
//   // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne
//   await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//   const formData = new FormData();
//   formData.append('file', file); // Dodanie pliku do formData
//
//   try {
//     const response = await ApiService.post('/api/admin/image-upload', formData, {
//       headers: ApiService.getHeadersJsonUTF8OrDifferent('multipart/form-data'),
//     });
//     return response.data; // Zwróć ciąg znaków zwrócony przez endpoint
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };


///-------------------------------------------------------------------------
/// notifications

// ApiService.getNotifications = async (sortBy, sortDirection, keyword, pageNumber) => {
//     // Sprawdzenie ważności tokenów i odświeżenie, jeśli to konieczne lub usuniecie danych o tokenach i przekierowanie na stronę logowania
//     await ApiService.checkTokenExpiration();
//
//   try {
//     const payload = {
//       ...(sortBy !== null && sortDirection !== null && { sortBy }),
//       ...(sortBy !== null && sortDirection !== null && { sortDirection }),
//       ...(keyword && { keyword }),
//       pageNumber
//     }
//     const response = await ApiService.get('/api/admin/notifications', {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       params: payload,
//       });
//     return response.data; // Zwróć dane użytkowników
//   } catch (error) {
//     throw error; // Obsługa błędów jest już zaimplementowana w interceptorze
//   }
// };
//
// ApiService.updateNotification = async (notificationData) => {
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const response = await ApiService.patch('/api/admin/notifications', notificationData, {
//       headers: ApiService.getHeadersJsonUTF8OrDifferent(),
//     });
//     return response.data;
//   } catch (error) {
//     throw error;
//   }
// };
//
// ApiService.addNewNotification = async (notificationData) => {
//   await ApiService.checkTokenExpiration();
//
//   try {
//     const response = await ApiService.post('/api/admin/notifications', notificationData, {
//       headers: ApiService.getHeadersJsonUTF8OrDifferent(),
//       });
//     return response.data;
//   } catch (error) {
//     throw error;
//   }
// };
//
// ApiService.deleteNotifByIdList = async (idList) => {
//   await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//
//   try {
//     const response = await ApiService.delete('/api/admin/notifications', {
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//       data: idList, // Lista ID w ciele zapytania
//     });
//
//     return response // Zwróć odpowiedź serwera
//   } catch (error) {
//     throw error; // Obsłuż błędy w miejscu wywołania
//   }
// };
//
// ApiService.getNotificationTypes = async () => {
// await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//
//   try {
//     const response = await ApiService.get('/api/admin/notifications/types',
//       {
//       headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//       });
//     return response.data // Zwróć odpowiedź serwera
//   } catch (error) {
//     throw error; // Obsłuż błędy w miejscu wywołania
//   }
// };
//
// ApiService.getStatistics = async () => {
//   await ApiService.checkTokenExpiration();
//
//   const token = store.getState().tokens.accessToken;
//
//   try {
//     const response = await ApiService.get('/api/admin/activity',
//         {
//           headers: ApiService.getHeaderOnlyToken(), // Dodanie tokenu do nagłówka
//         });
//     return response.data // Zwróć odpowiedź serwera
//   } catch (error) {
//     throw error; // Obsłuż błędy w miejscu wywołania
//   }
// };

///-------------------------------------------------------------------------
// promotions (admin)

ApiService.getAdminPromotions = async (pageNumber = 1, pageSize = 10, sortBy = null, sortDirection = null, status = null, promotionType = null, organizerId = null, dateFrom = null, dateTo = null) => {
    await ApiService.checkTokenExpiration();
    const response = await ApiService.get('/api/admin/promotions', {
        headers: ApiService.getHeaderOnlyToken(),
        params: {
            pageNumber,
            pageSize,
            ...(sortBy && { sortBy }),
            ...(sortDirection && { sortDirection }),
            ...(status && { status }),
            ...(promotionType && { promotionType }),
            ...(organizerId && { organizerId }),
            ...(dateFrom && { dateFrom }),
            ...(dateTo && { dateTo }),
        },
    });
    return response.data;
};

ApiService.createAdminPromotion = async (payload) => {
    await ApiService.checkTokenExpiration();
    const response = await ApiService.post('/api/admin/promotions', payload, {
        headers: ApiService.getHeadersJsonUTF8OrDifferent(),
    });
    return response.data;
};

ApiService.deleteAdminPromotion = async (promotionId) => {
    await ApiService.checkTokenExpiration();
    const response = await ApiService.delete(`/api/admin/promotions/${promotionId}`, {
        headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
};

ApiService.getAdminPromotionStats = async () => {
    await ApiService.checkTokenExpiration();
    const response = await ApiService.get('/api/admin/promotions/stats', {
        headers: ApiService.getHeaderOnlyToken(),
    });
    return response.data;
};

ApiService.getB2bStatisticsSummary = async (dateFrom = null, dateTo = null) => {
    await ApiService.checkTokenExpiration();
    const response = await ApiService.get('/api/v1/statistics/b2b/summary', {
        headers: ApiService.getHeaderOnlyToken(),
        params: {
            ...(dateFrom && { dateFrom }),
            ...(dateTo && { dateTo }),
        },
    });
    return response.data;
};

ApiService.getB2bStatisticsSeries = async (metricType, interval = 'MONTH', dateFrom = null, dateTo = null) => {
    await ApiService.checkTokenExpiration();
    const response = await ApiService.get(`/api/v1/statistics/b2b/series/${metricType}`, {
        headers: ApiService.getHeaderOnlyToken(),
        params: {
            interval,
            ...(dateFrom && { dateFrom }),
            ...(dateTo && { dateTo }),
        },
    });
    return response.data;
};

export default ApiService;
